import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { 
  Phone, 
  Plus, 
  Calendar, 
  ArrowRight, 
  Pencil, 
  Trash2,
  X,
  Loader2,
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useLeadCallLogs, type LeadCallLog } from "@/hooks/useLeadCallLogs";
import { type Lead } from "@/hooks/useAdminLeads";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CallAudioRecorder } from "@/components/admin/CallAudioRecorder";
import { CallAudioPlayer } from "@/components/admin/CallAudioPlayer";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface LeadCallLogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: Lead | null;
  onCallLogAdded?: () => void;
}

export function LeadCallLogDialog({ 
  open, 
  onOpenChange, 
  lead,
  onCallLogAdded,
}: LeadCallLogDialogProps) {
  const { callLogs, loading, fetchCallLogs, addCallLog, updateCallLog, deleteCallLog } = useLeadCallLogs();
  const { toast } = useToast();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLog, setEditingLog] = useState<LeadCallLog | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [logToDelete, setLogToDelete] = useState<LeadCallLog | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [callDate, setCallDate] = useState("");
  const [callTime, setCallTime] = useState("");
  const [transcript, setTranscript] = useState("");
  const [nextSteps, setNextSteps] = useState("");

  // Audio state from CallAudioRecorder
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioDurationSeconds, setAudioDurationSeconds] = useState(0);
  const [wasTranscribed, setWasTranscribed] = useState(false);

  useEffect(() => {
    if (open && lead) {
      fetchCallLogs(lead.id);
      resetForm();
    }
  }, [open, lead, fetchCallLogs]);

  const resetForm = () => {
    setShowAddForm(false);
    setEditingLog(null);
    setCallDate(format(new Date(), "yyyy-MM-dd"));
    setCallTime(format(new Date(), "HH:mm"));
    setTranscript("");
    setNextSteps("");
    setAudioBlob(null);
    setAudioDurationSeconds(0);
    setWasTranscribed(false);
  };

  const handleAddNew = () => {
    setEditingLog(null);
    setCallDate(format(new Date(), "yyyy-MM-dd"));
    setCallTime(format(new Date(), "HH:mm"));
    setTranscript("");
    setNextSteps("");
    setAudioBlob(null);
    setAudioDurationSeconds(0);
    setWasTranscribed(false);
    setShowAddForm(true);
  };

  const handleEdit = (log: LeadCallLog) => {
    setEditingLog(log);
    const date = new Date(log.call_date);
    setCallDate(format(date, "yyyy-MM-dd"));
    setCallTime(format(date, "HH:mm"));
    setTranscript(log.transcript || "");
    setNextSteps(log.next_steps || "");
    setAudioBlob(null);
    setAudioDurationSeconds(0);
    setWasTranscribed(false);
    setShowAddForm(true);
  };

  const uploadAudioIfAny = async (leadId: string): Promise<{ path: string; duration: number } | null> => {
    if (!audioBlob) return null;
    const ext = audioBlob.type.includes("webm") ? "webm" : "audio";
    const fileName = `${crypto.randomUUID()}.${ext}`;
    const path = `${leadId}/${fileName}`;
    const { error } = await supabase.storage
      .from('call-recordings')
      .upload(path, audioBlob, { contentType: audioBlob.type || 'audio/webm', upsert: false });
    if (error) {
      throw new Error(`Upload audio échoué : ${error.message}`);
    }
    return { path, duration: audioDurationSeconds };
  };

  const handleSave = async () => {
    if (!lead) return;

    const callDateTime = new Date(`${callDate}T${callTime}`).toISOString();

    try {
      setSaving(true);
      let audioInfo: { path: string; duration: number } | null = null;
      try {
        audioInfo = await uploadAudioIfAny(lead.id);
      } catch (err) {
        toast({
          title: "Erreur",
          description: err instanceof Error ? err.message : "Upload audio échoué",
          variant: "destructive",
        });
        return;
      }

      const transcribedAt = wasTranscribed ? new Date().toISOString() : null;

      if (editingLog) {
        const success = await updateCallLog(editingLog.id, {
          call_date: callDateTime,
          transcript,
          next_steps: nextSteps,
          ...(audioInfo ? {
            audio_path: audioInfo.path,
            audio_duration_seconds: audioInfo.duration,
            transcribed_at: transcribedAt,
          } : {}),
        });
        if (success) {
          resetForm();
          onCallLogAdded?.();
        }
      } else {
        const success = await addCallLog({
          lead_id: lead.id,
          call_date: callDateTime,
          transcript,
          next_steps: nextSteps,
          audio_path: audioInfo?.path ?? null,
          audio_duration_seconds: audioInfo?.duration ?? null,
          transcribed_at: transcribedAt,
        });
        if (success) {
          resetForm();
          onCallLogAdded?.();
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (log: LeadCallLog) => {
    setLogToDelete(log);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (logToDelete) {
      await deleteCallLog(logToDelete.id);
      setDeleteConfirmOpen(false);
      setLogToDelete(null);
      onCallLogAdded?.();
    }
  };

  const handleTranscribed = (newTranscript: string, newNextSteps: string) => {
    if (newTranscript) setTranscript(newTranscript);
    if (newNextSteps) setNextSteps(newNextSteps);
  };

  if (!lead) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col min-h-0">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-primary" />
              Journal d'appels - {lead.full_name}
            </DialogTitle>
            <DialogDescription>
              Historique des appels et discussions avec ce prospect
            </DialogDescription>
          </DialogHeader>

          {showAddForm ? (
            <div className="space-y-4 py-4 flex-1 min-h-0 overflow-y-auto">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">
                  {editingLog ? "Modifier l'appel" : "Nouveau compte-rendu d'appel"}
                </h3>
                <Button variant="ghost" size="sm" onClick={resetForm} disabled={saving}>
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="callDate">Date de l'appel</Label>
                  <Input
                    id="callDate"
                    type="date"
                    value={callDate}
                    onChange={(e) => setCallDate(e.target.value)}
                    disabled={saving}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="callTime">Heure</Label>
                  <Input
                    id="callTime"
                    type="time"
                    value={callTime}
                    onChange={(e) => setCallTime(e.target.value)}
                    disabled={saving}
                  />
                </div>
              </div>

              <CallAudioRecorder
                onTranscribed={handleTranscribed}
                onAudioChange={({ blob, durationSeconds, transcribed }) => {
                  setAudioBlob(blob);
                  setAudioDurationSeconds(durationSeconds);
                  setWasTranscribed(transcribed);
                }}
                disabled={saving}
              />

              <div className="space-y-2">
                <Label htmlFor="transcript">Résumé de la discussion</Label>
                <Textarea
                  id="transcript"
                  placeholder="Points abordés, besoins exprimés, objections..."
                  value={transcript}
                  onChange={(e) => setTranscript(e.target.value)}
                  rows={5}
                  disabled={saving}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="nextSteps">Prochaines étapes</Label>
                <Textarea
                  id="nextSteps"
                  placeholder="Actions à effectuer, date de rappel..."
                  value={nextSteps}
                  onChange={(e) => setNextSteps(e.target.value)}
                  rows={3}
                  disabled={saving}
                />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={resetForm} disabled={saving}>
                  Annuler
                </Button>
                <Button onClick={handleSave} disabled={!callDate || saving}>
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingLog ? "Enregistrer" : "Ajouter"}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              <div className="flex justify-end shrink-0">
                <Button onClick={handleAddNew} size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter un appel
                </Button>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6 pr-4">
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Chargement...
                  </div>
                ) : callLogs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Phone className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>Aucun appel enregistré</p>
                    <p className="text-sm">Cliquez sur "Ajouter un appel" pour créer le premier compte-rendu</p>
                  </div>
                ) : (
                  <div className="space-y-4 pb-4">
                    {callLogs.map((log, index) => (
                      <Card key={log.id} className="relative">
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm">
                              <Calendar className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium">
                                {format(new Date(log.call_date), "EEEE d MMMM yyyy 'à' HH:mm", { locale: fr })}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleEdit(log)}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={() => handleDeleteClick(log)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {log.audio_path && (
                            <CallAudioPlayer
                              audioPath={log.audio_path}
                              durationSeconds={log.audio_duration_seconds}
                            />
                          )}
                          {log.transcript && (
                            <div>
                              <p className="text-xs text-muted-foreground font-medium mb-1">Résumé</p>
                              <p className="text-sm whitespace-pre-wrap">{log.transcript}</p>
                            </div>
                          )}
                          {log.next_steps && (
                            <div>
                              <p className="text-xs text-muted-foreground font-medium mb-1 flex items-center gap-1">
                                <ArrowRight className="w-3 h-3" />
                                Prochaines étapes
                              </p>
                              <p className="text-sm whitespace-pre-wrap">{log.next_steps}</p>
                            </div>
                          )}
                          {!log.transcript && !log.next_steps && !log.audio_path && (
                            <p className="text-sm text-muted-foreground italic">
                              Aucune note pour cet appel
                            </p>
                          )}
                        </CardContent>
                        {index < callLogs.length - 1 && <Separator />}
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet appel ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le compte-rendu d'appel sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
